import PropTypes from 'prop-types'
import styled from 'styled-components'
import { color, space, shadow, typography, variant, layout } from 'styled-system'

const ButtonBase = styled.button`
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  max-width: 340px;
  text-align: center;
  margin-left:5px;
  margin-right:5px;
  word-break:normal;

  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  border-radius: 8px;
  text-decoration: none;

  :disabled,
  :disabled:hover {
    background-color: ${props => props.theme.colors.grays.N300};
    box-shadow: none;
    cursor: not-allowed;
  }

  ${color}
  ${space}
  ${shadow}
  ${typography}
  ${layout}
`
ButtonBase.defaultProps = {
  fontFamily: 'body',
  fontSize: 'button',
  fontWeight: 'bold',
  py: '5px',
  px: '7px',
}

const Button = styled(ButtonBase)(
  ({ outlined }) =>
    variant({
      variants: {
        primary: {
          bg: () => (outlined ? 'transparent' : 'primary'),
          color: () => (outlined ? 'primary' : 'white'),
          borderColor: () => (outlined ? 'primary' : 'transparent'),
          boxShadow: 'buttons.default',

          '&:hover': {
            bg: () => (outlined ? 'transparent' : 'turquoises.T600'),
            color: () => (outlined ? 'turquoises.T600' : 'white'),
            borderColor: () => (outlined ? 'turquoises.T600' : 'transparent'),
            boxShadow: 'buttons.hover',
          },
        },

        secondary: {
          bg: () => (outlined ? 'transparent' : 'secondary'),
          color: () => (outlined ? 'secondary' : 'white'),
          borderColor: () => (outlined ? 'secondary' : 'transparent'),
          boxShadow: 'buttons.default',

          '&:hover': {
            bg: () => (outlined ? 'transparent' : 'blues.A900'),
            color: () => (outlined ? 'blues.A900' : 'white'),
            borderColor: () => (outlined ? 'blues.A900' : 'transparent'),
            boxShadow: 'buttons.hover',
          },
        },

        tertiary: {
          bg: () => (outlined ? 'transparent' : 'oranges.L400'),
          color: () => (outlined ? 'oranges.L400' : 'white'),
          borderColor: () => (outlined ? 'oranges.L400' : 'transparent'),
          boxShadow: 'buttons.default',

          '&:hover': {
            bg: () => (outlined ? 'transparent' : 'oranges.L500'),
            color: () => (outlined ? 'oranges.L500' : 'white'),
            borderColor: () => (outlined ? 'oranges.L500' : 'transparent'),
            boxShadow: 'buttons.hover',
          },
        },
        ghost: {
          bg: 'transparent',
          border: 'none',
          padding: 0,
          outline: 'none',
        },
        warning: {
          bg: () => (outlined ? 'transparent' : 'rgb(204,0,0)'),
          color: () => (outlined ? 'rgb(204,0,0)' : 'white'),
          borderColor: () => (outlined ? 'rgb(204,0,0)' : 'transparent'),
          boxShadow: 'buttons.default',

          '&:hover': {
            bg: () => (outlined ? 'transparent' : 'rgba(204,51,0)'),
            color: () => (outlined ? 'rgba(204,51,0)' : 'white'),
            borderColor: () => (outlined ? 'rgba(204,51,0)' : 'transparent'),
            boxShadow: 'buttons.hover',
          },
        },
      },
    }),

  /**
   * Size Variants
   */
  variant({
    prop: 'size',
    variants: {
      smallest: {
        width: 'auto',
        minWidth: 0,
        fontSize: '1em',
      },
      small: {
        width: '120px',
      },
      normal: {
        width: '180px',
      },
      big: {
        width: '280px',
      },
      auto: {
        width: '100%',
      },
    },
  }),
)

Button.defaultProps = {
  variant: 'primary',
  outlined: false,
  size: 'normal',
}

Button.propTypes = {
  size: PropTypes.oneOf(['micro', 'smallest', 'small', 'normal', 'big', 'auto']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'ghost', 'warning']),
  outlined: PropTypes.bool,
}

export default Button
